import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  PiClock,
  PiShieldCheck,
  PiArrowClockwise,
  PiSignOut,
  PiCheckCircle,
  PiWarning,
  PiWarningCircle,
  PiInfo,
  PiUser,
  PiEnvelope,
  PiIdentificationBadge,
  PiDesktop
} from 'react-icons/pi';
import { useAuth } from '../auth/AuthProvider';

interface SessionInfo {
  sessionId: string;
  userId: string;
  email: string;
  createdAt: string;
  lastActivity: string;
  expiresAt: string;
  ipAddress: string;
  userAgent: string;
  isActive: boolean;
  timeRemaining: number;
}

export const SessionManagement: React.FC = () => {
  const { user, sessionStatus, getCurrentSession, extendSession, refreshToken, signOut } = useAuth();
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [extending, setExtending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);

  useEffect(() => {
    loadSessionInfo();
    const interval = setInterval(loadSessionInfo, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (sessionInfo) {
      const interval = setInterval(() => {
        const now = Date.now();
        const expiry = new Date(sessionInfo.expiresAt).getTime();
        const remaining = Math.max(0, expiry - now);
        setTimeRemaining(remaining);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [sessionInfo]);

  const loadSessionInfo = async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const session = await getCurrentSession();
      if (session) {
        setSessionInfo(session);
      }
    } catch (err) {
      setError('Failed to load session information');
      console.error('Session load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExtendSession = async () => {
    setExtending(true);
    setError(null);
    
    try {
      const success = await extendSession();
      if (success) {
        await loadSessionInfo(); // Refresh session info
      } else {
        setError('Failed to extend session');
      }
    } catch (err) {
      setError('Error extending session');
      console.error('Extend session error:', err);
    } finally {
      setExtending(false);
    }
  };

  const handleRefreshToken = async () => {
    setRefreshing(true);
    setError(null);
    
    try {
      const success = await refreshToken();
      if (success) {
        await loadSessionInfo(); // Refresh session info
      } else {
        setError('Failed to refresh token');
      }
    } catch (err) {
      setError('Error refreshing token');
      console.error('Refresh token error:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleSignOut = () => {
    setShowLogoutDialog(false);
    signOut();
  };

  const getStatusColor = () => {
    switch (sessionStatus) {
      case 'active': return 'success';
      case 'expired': return 'error';
      case 'inactive': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = () => {
    switch (sessionStatus) {
      case 'active': return <PiCheckCircle size={24} />;
      case 'expired': return <PiWarningCircle size={24} />;
      case 'inactive': return <PiWarning size={24} />;
      default: return <PiInfo size={24} />;
    }
  };

  const formatTimeRemaining = (ms: number) => {
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((ms % (1000 * 60)) / 1000);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const getTimeRemainingProgress = () => {
    if (!sessionInfo) return 0;
    const total = 8 * 60 * 60 * 1000; // 8 hours in ms
    return Math.max(0, Math.min(100, (timeRemaining / total) * 100));
  };

  const formatUserAgent = (agent?: string) => {
    if (!agent) {
      return 'Unknown';
    }
    const [primaryToken] = agent.split(/\s+/);
    return primaryToken || agent;
  };

  if (!user) {
    return (
      <Card>
        <CardContent>
          <Typography>Please sign in to view session information.</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ maxWidth: '800px', mx: 'auto', p: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Session Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Session Status Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {getStatusIcon()}
                <Typography variant="h6" sx={{ ml: 1 }}>
                  Session Status
                </Typography>
              </Box>
              
              <Chip 
                label={sessionStatus.charAt(0).toUpperCase() + sessionStatus.slice(1)}
                color={getStatusColor()}
                sx={{ mb: 2 }}
              />

              {timeRemaining > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Time Remaining
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatTimeRemaining(timeRemaining)}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={getTimeRemainingProgress()}
                    color={timeRemaining < 30 * 60 * 1000 ? 'warning' : 'primary'} // Warning if less than 30 minutes
                  />
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PiArrowClockwise size={18} />}
                  onClick={loadSessionInfo}
                  disabled={loading}
                >
                  Refresh
                </Button>
                {sessionStatus === 'active' && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<PiClock size={18} />}
                    onClick={handleExtendSession}
                    disabled={extending}
                  >
                    {extending ? 'Extending...' : 'Extend Session'}
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* User Information Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                User Information
              </Typography>
              
              <List dense>
                <ListItem disablePadding>
                  <ListItemIcon><PiUser size={18} /></ListItemIcon>
                  <ListItemText 
                    primary="Username" 
                    secondary={user.username}
                  />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon><PiEnvelope size={18} /></ListItemIcon>
                  <ListItemText 
                    primary="Email" 
                    secondary={user.email}
                  />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon><PiIdentificationBadge size={18} /></ListItemIcon>
                  <ListItemText 
                    primary="User ID" 
                    secondary={user.sub}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Session Details Card */}
        {sessionInfo && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Session Details
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PiClock size={18} style={{ marginRight: 8 }} />
                        <Typography variant="subtitle2">Session Times</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Created: {new Date(sessionInfo.createdAt).toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Last Activity: {new Date(sessionInfo.lastActivity).toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Expires: {new Date(sessionInfo.expiresAt).toLocaleString()}
                      </Typography>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PiDesktop size={18} style={{ marginRight: 8 }} />
                        <Typography variant="subtitle2">Connection Info</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        IP Address: {sessionInfo.ipAddress}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                        Browser: {formatUserAgent(sessionInfo.userAgent)}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Actions Card */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Token Management
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<PiShieldCheck size={18} />}
                  onClick={handleRefreshToken}
                  disabled={refreshing}
                >
                  {refreshing ? 'Refreshing...' : 'Refresh Access Token'}
                </Button>
                
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<PiSignOut size={18} />}
                  onClick={() => setShowLogoutDialog(true)}
                >
                  Sign Out
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Logout Confirmation Dialog */}
      <Dialog open={showLogoutDialog} onClose={() => setShowLogoutDialog(false)}>
        <DialogTitle>Confirm Sign Out</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to sign out? You will need to authenticate again to access the application.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLogoutDialog(false)}>Cancel</Button>
          <Button onClick={handleSignOut} color="error" variant="contained">
            Sign Out
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};